import { createContext } from 'react';
import type { Backend, Preferences, Profile, ProfileDraft } from '@/ports';
import type { DateKey } from '@/domain/date';
import type { PrayerTimeSet, PrayerTimesProvider, TimesSettings } from '@bonyaan/prayer-times';
import type { Translator } from '@/i18n/translator';
import type { Scheme, Skin } from '@/theme/types';

export interface AppStateValue {
  ready: boolean;
  backend: Backend;
  profile: Profile | null;
  preferences: Preferences;
  /** Resolved from the profile's age unless someone overrode it. */
  skin: Skin;
  /** Resolved from the preference, with 'auto' turned into a real scheme. */
  scheme: Scheme;
  times: PrayerTimeSet;
  /**
   * The wall clock, re-read once a minute and whenever the tab wakes. Screens
   * that show something time-dependent — which prayer we are in, whether one
   * is due — read this rather than calling `new Date()` in a render, so they
   * actually move with the clock instead of only with the next interaction.
   */
  now: Date;
  /**
   * The day a prayer recorded now belongs to — today once Fajr has come in,
   * yesterday until it does. Every screen reads this rather than the calendar
   * date, so the five always sit in one day in the order they are prayed.
   */
  today: DateKey;
  /** Which provider produced them — 'placeholder' until a location is set. */
  timesProvider: PrayerTimesProvider;
  i18n: Translator;
  /**
   * Show a skin without committing to it. Onboarding uses this so the whole
   * screen re-skins as the age changes — the rule is easier to believe when
   * you watch it happen than when a card describes it.
   */
  previewSkin: (skin: Skin | null) => void;
  saveProfile: (draft: ProfileDraft) => Promise<void>;
  updatePreferences: (patch: Partial<Preferences>) => Promise<void>;
  /**
   * Merge into the prayer-time settings specifically. Needed because they are
   * a nested object: a caller holding a stale copy — a geolocation callback
   * that started before the person changed the method, say — would otherwise
   * write the whole object back and quietly undo their choice.
   */
  updateTimes: (patch: Partial<TimesSettings>) => Promise<void>;
}

export const AppStateContext = createContext<AppStateValue | null>(null);
